import type {
  AppClosePayload,
  AuthEvent,
  ChildToParentEvent,
  ParentToChildEvent,
  StatusPayload,
} from './types';

export type DigitalMirrorAuthConfig = {
  userId: string;
  password: string;
  storeId: string;
  deviceId: string;
};

export type DigitalMirrorClientOptions = {
  origin: string;
  auth: DigitalMirrorAuthConfig;
  onStatus?: (payload: StatusPayload) => void;
  onClose?: (payload: AppClosePayload) => void;
  onError?: (err: Error) => void;
  debug?: boolean;
};

export type DigitalMirrorClient = {
  mount(iframeEl: HTMLIFrameElement): void;
  destroy(): void;
  checkStatus(): void;
  sendProducts(productIds: string[]): void;
  sendMember(memberId: string): void;
  isReady(): boolean;
};

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

const normalizeOrigin = (raw: string): string => {
  // event.origin never includes paths/trailing slashes; normalize to URL.origin
  return new URL(raw).origin;
};

export function createDigitalMirrorClient(options: DigitalMirrorClientOptions): DigitalMirrorClient {
  const origin = normalizeOrigin(options.origin);
  const debugEnabled = Boolean(options.debug);
  const debug = (...args: any[]) => {
    if (!debugEnabled) return;
    // eslint-disable-next-line no-console
    console.log('[DigitalMirror]', ...args);
  };

  let iframeEl: HTMLIFrameElement | null = null;
  let mounted = false;
  let ready = false;
  let authSent = false;
  const queue: ParentToChildEvent[] = [];

  const safeOnError = (err: unknown) => {
    const e = err instanceof Error ? err : new Error(String(err));
    options.onError?.(e);
    debug('error', e);
  };

  const postToChild = (msg: ParentToChildEvent) => {
    if (!iframeEl?.contentWindow) {
      debug('postMessage skipped (no iframe window)', msg.event);
      return;
    }
    try {
      iframeEl.contentWindow.postMessage(msg, origin);
      debug('postMessage ->', msg.event, msg);
    } catch (e) {
      safeOnError(e);
    }
  };

  const flushQueue = () => {
    if (!ready) return;
    while (queue.length > 0) {
      const msg = queue.shift();
      if (!msg) break;
      postToChild(msg);
    }
  };

  const sendAuthOnce = () => {
    if (authSent) return;
    authSent = true;
    const authMsg: AuthEvent = {
      event: 'AUTH',
      payload: {
        userId: String(options.auth.userId),
        password: String(options.auth.password),
        storeId: String(options.auth.storeId),
        deviceId: String(options.auth.deviceId),
        deviceType: 'iframe',
      },
    };
    postToChild(authMsg);
  };

  const onMessage = (evt: MessageEvent) => {
    if (!mounted || !iframeEl) return;

    // Security requirements: origin must match exactly.
    if (evt.origin !== origin) return;

    // Extra safety: only accept messages from our iframe window (if available).
    const srcWin = iframeEl.contentWindow;
    if (srcWin && evt.source !== srcWin) return;

    const data = evt.data as unknown;
    if (!isRecord(data)) return;
    if (typeof (data as any).event !== 'string') return;

    const eventName = String((data as any).event);
    if (eventName === 'STATUS') {
      const payload = (data as any).payload as unknown;
      if (!isRecord(payload)) return;
      const status = (payload as any).status as unknown;
      if (status !== 'loading' && status !== 'ready' && status !== 'error') return;

      const statusPayload: StatusPayload = {
        status,
        message: typeof (payload as any).message === 'string' ? String((payload as any).message) : undefined,
      };

      options.onStatus?.(statusPayload);
      debug('STATUS <-', statusPayload);

      if (statusPayload.status === 'loading') {
        // Must send AUTH only after receiving STATUS:loading (not on iframe onload).
        sendAuthOnce();
        return;
      }

      if (statusPayload.status === 'ready') {
        ready = true;
        flushQueue();
        return;
      }

      if (statusPayload.status === 'error') {
        safeOnError(statusPayload.message ? new Error(statusPayload.message) : new Error('DigitalMirror STATUS:error'));
        return;
      }

      return;
    }

    if (eventName === 'APP_CLOSE') {
      const payload = (data as any).payload as unknown;
      const reason = isRecord(payload) ? (payload as any).reason : undefined;
      const closePayload: AppClosePayload = {
        reason:
          reason === 'user_exit' || reason === 'timeout' || reason === 'error' ? (reason as any) : undefined,
      };
      debug('APP_CLOSE <-', closePayload);
      options.onClose?.(closePayload);
      return;
    }

    // Unknown event: ignore safely
    const _unused: ChildToParentEvent | null = null;
    void _unused;
  };

  return {
    mount(el: HTMLIFrameElement) {
      if (!el) throw new Error('mount(iframeEl) is required');

      // If remounting, clean up the old listener/state.
      if (mounted) {
        window.removeEventListener('message', onMessage as any);
      }

      iframeEl = el;
      mounted = true;
      ready = false;
      authSent = false;
      queue.splice(0, queue.length);

      window.addEventListener('message', onMessage as any);
      debug('mounted', { origin });
    },

    destroy() {
      if (mounted) {
        window.removeEventListener('message', onMessage as any);
      }
      mounted = false;
      iframeEl = null;
      ready = false;
      authSent = false;
      queue.splice(0, queue.length);
      debug('destroyed');
    },

    checkStatus() {
      if (!mounted) {
        safeOnError(new Error('checkStatus() called before mount()'));
        return;
      }
      postToChild({ event: 'STATUS_CHECK' });
    },

    sendProducts(productIds: string[]) {
      const ids = Array.isArray(productIds)
        ? productIds.map((s) => String(s ?? '').trim()).filter(Boolean)
        : [];
      const msg: ParentToChildEvent = { event: 'PRODUCTS', payload: { productIds: ids } };

      if (ready) {
        postToChild(msg);
      } else {
        queue.push(msg);
        debug('queued PRODUCTS', { count: ids.length });
      }
    },

    sendMember(memberId: string) {
      const id = String(memberId ?? '').trim();
      if (!id) return;
      const msg: ParentToChildEvent = { event: 'MEMBER', payload: { memberId: id } };

      if (ready) {
        postToChild(msg);
      } else {
        queue.push(msg);
        debug('queued MEMBER');
      }
    },

    isReady() {
      return ready;
    },
  };
}
